import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../utils/Instance';
import { toast } from 'sonner';

/**
 * W9Form Component - TaxZerone W-9 Integration
 * 
 * Implements Option A: Popup/Modal Flow (Recommended)
 * - W-9 opens in popup or modal window
 * - Creator stays on client platform
 * - After completion: popup closes, creator proceeds
 * 
 * Props:
 * - onCompletion: Callback when W-9 is completed
 * - showModal: Whether to show as modal (default: false)
 * - onClose: Callback to close modal
 * - signupFormData: Optional form data for signup flow (when user is not logged in)
 * 
 * Based on TaxZerone documentation:
 * https://selectexposure.zeronevault.com/w9forms/?uniqueid={unique_id}
 */
export default function W9Form({ onCompletion, showModal = false, onClose, signupFormData = null }) {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [w9Status, setW9Status] = useState({
    w9_completed: false,
    w9_unique_id: null,
    w9_url: null,
  });
  
  // Check if we're in signup mode (no auth required)
  const isSignupMode = !!signupFormData && (!profile || !profile.accessToken);

  const fetchW9Status = useCallback(async () => {
    try {
      if (!profile || !profile.accessToken) {
        console.warn('Cannot fetch W-9 status: profile or accessToken missing');
        return;
      }

      const url = `${API_BASE}accounts/profile/w9/status/`;
      console.log('Fetching W-9 status from:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${profile.accessToken}`,
        },
      });

      console.log('W-9 status response:', response.status, response.statusText);

      if (response.ok) {
        const data = await response.json();
        console.log('W-9 status data:', data);
        setW9Status(data);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to fetch W-9 status:', errorData);
      }
    } catch (error) {
      console.error('Failed to fetch W-9 status:', error);
    }
  }, [profile]);

  // Generate W-9 URL from signup form data via API (requires server-generated unique ID)
  const generateW9UrlFromFormData = useCallback(async (formData) => {
    try {
      const url = `${API_BASE}accounts/w9/generate-for-signup/`;
      console.log('Calling W-9 signup API:', url);
      console.log('Form data being sent:', {
        email: formData.email,
        legal_full_name: formData.legal_full_name,
        first_name: formData.first_name,
        last_name: formData.last_name,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code,
        country: formData.country,
        phone_number: formData.phone_number,
      });
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          legal_full_name: formData.legal_full_name,
          first_name: formData.first_name,
          last_name: formData.last_name,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zip_code,
          country: formData.country,
          phone_number: formData.phone_number,
        }),
      });

      console.log('W-9 signup API response:', response.status, response.statusText);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('W-9 signup API Error:', errorData);
        throw new Error(errorData.error || 'Failed to generate W-9 form link');
      }

      const data = await response.json();
      console.log('W-9 signup API data:', data);
      console.log('Generated W-9 URL:', data.w9_url);
      console.log('Unique ID:', data.unique_id);
      
      // Store unique ID in localStorage temporarily so it can be linked after registration
      if (data.unique_id) {
        localStorage.setItem('w9_signup_unique_id', data.unique_id);
      }
      
      return { uniqueId: data.unique_id, w9Url: data.w9_url };
    } catch (error) {
      console.error('Failed to generate W-9 URL from form data:', error);
      toast.error(error.message || 'Failed to generate W-9 form link');
      return null;
    }
  }, []);

  // Fetch W-9 status on mount (only if logged in)
  useEffect(() => {
    if (isSignupMode) {
      // In signup mode, don't auto-generate URL - wait for user to click button
      // This avoids unnecessary API calls
      setW9Status({
        w9_completed: false,
        w9_unique_id: null,
        w9_url: null,
      });
    } else if (profile?.accessToken) {
      fetchW9Status();
    } else {
      console.warn('W9Form: No profile or accessToken available', { profile });
    }
  }, [profile, fetchW9Status, isSignupMode]);

  const generateUniqueId = async () => {
    try {
      if (!profile || !profile.accessToken) {
        console.error('Cannot generate unique ID: profile or accessToken missing');
        toast.error('Please log in to access W-9 form');
        return null;
      }

      setLoading(true);
      const url = `${API_BASE}accounts/profile/w9/generate-unique-id/`;
      console.log('Calling API:', url);
      console.log('Headers:', {
        'Authorization': `Bearer ${profile.accessToken?.substring(0, 20)}...`,
        'Content-Type': 'application/json',
      });

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${profile.accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API Error:', errorData);
        throw new Error(errorData.error || 'Failed to generate W-9 unique ID');
      }

      const data = await response.json();
      console.log('API Response:', data);
      setW9Status({
        ...w9Status,
        w9_unique_id: data.unique_id,
        w9_url: data.w9_url,
      });
      return data.w9_url;
    } catch (error) {
      console.error('Failed to generate unique ID:', error);
      toast.error(error.message || 'Failed to generate W-9 form link');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const openW9Popup = async () => {
    try {
      console.log('Opening W-9 popup...', { w9Status, hasUrl: !!w9Status.w9_url, isSignupMode });

      // In signup mode, use API to generate URL with proper unique ID
      if (isSignupMode) {
        if (!signupFormData) {
          toast.error('Form data is required for W-9 form');
          return;
        }
        
        setLoading(true);
        const result = await generateW9UrlFromFormData(signupFormData);
        
        if (!result || !result.w9Url) {
          setLoading(false);
          return;
        }
        
        setW9Status({
          ...w9Status,
          w9_unique_id: result.uniqueId,
          w9_url: result.w9Url,
        });
        
        console.log('Generated W-9 URL:', result.w9Url);
        console.log('Unique ID:', result.uniqueId);
        
        // Open popup with the generated URL
        const width = 800;
        const height = 900;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;

        const popup = window.open(
          result.w9Url,
          'W9Form',
          `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );

        if (!popup) {
          toast.error('Popup blocked. Please allow popups for this site.');
          setLoading(false);
          return;
        }

        setLoading(false);

        // Poll for popup closure
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            // Refresh status after popup closes
            setTimeout(() => {
              if (onCompletion) {
                onCompletion();
              }
            }, 1000);
          }
        }, 500);

        toast.success('W-9 form opened in popup. Complete the form and close the popup when done.');
        
        // Show helpful message about TaxZerone configuration
        setTimeout(() => {
          console.log('⚠️ TaxZerone Configuration Required:');
          console.log('If you see "You may no longer have the required permissions" error:');
          console.log('1. Contact TaxZerone support to configure your account');
          console.log('2. Request them to whitelist dynamically generated unique IDs');
          console.log('3. Verify domain configuration: selectexposure.zeronevault.com');
          console.log('4. Generated Unique ID:', result.uniqueId);
          console.log('5. W-9 URL:', result.w9Url);
          
          toast.info(
            'If TaxZerone shows an error, contact TaxZerone support to configure your account. Check console for details.',
            { duration: 5000 }
          );
        }, 2000);
        
        return;
      }

      // Logged-in mode: Check if profile and accessToken exist
      if (!profile || !profile.accessToken) {
        console.error('Profile or accessToken missing:', { profile, hasToken: !!profile?.accessToken });
        toast.error('Please log in to access W-9 form');
        return;
      }

      // Generate unique ID if not exists
      let w9Url = w9Status.w9_url;
      if (!w9Url) {
        console.log('No W-9 URL found, generating unique ID...');
        w9Url = await generateUniqueId();
        if (!w9Url) {
          console.error('Failed to generate W-9 URL');
          return;
        }
        console.log('Generated W-9 URL:', w9Url);
      }

      // Open popup window
      const width = 800;
      const height = 900;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;

      const popup = window.open(
        w9Url,
        'W9Form',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
      );

      if (!popup) {
        toast.error('Popup blocked. Please allow popups for this site.');
        return;
      }

      // Poll for popup closure
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          // Refresh status after popup closes
          setTimeout(() => {
            fetchW9Status();
            if (onCompletion) {
              onCompletion();
            }
          }, 1000);
        }
      }, 500);

      toast.success('W-9 form opened in popup. Complete the form and close the popup when done.');
    } catch (error) {
      console.error('Failed to open W-9 popup:', error);
      toast.error('Failed to open W-9 form');
    }
  };

  // Modal version (if showModal is true)
  if (showModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
        <div className="bg-[#1c1c1e] rounded-lg shadow-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-[#CEB46A]">W-9 Tax Form</h3>
            {onClose && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          <div className="space-y-4">
            {w9Status.w9_completed ? (
              <div className="p-4 bg-green-900 bg-opacity-30 border border-green-700 rounded">
                <div className="flex items-center gap-2 text-green-400">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-semibold">W-9 Form Completed</span>
                </div>
                {w9Status.w9_completion_date && (
                  <p className="text-gray-400 text-sm mt-2">
                    Completed on: {new Date(w9Status.w9_completion_date).toLocaleDateString()}
                  </p>
                )}
              </div>
            ) : (
              <>
                <div className="p-4 bg-yellow-900 bg-opacity-30 border border-yellow-700 rounded">
                  <p className="text-yellow-200 text-sm mb-2">
                    <strong>Tax Information Required</strong>
                  </p>
                  <p className="text-gray-300 text-sm">
                    As a contributor, you must complete the W-9 form for tax purposes before receiving payments.
                    This form collects your legal name, TIN (SSN/EIN), entity type, and electronic signature.
                  </p>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={openW9Popup}
                    disabled={loading || (!isSignupMode && (!profile || !profile.accessToken)) || (isSignupMode && !signupFormData)}
                    className="w-full py-3 px-4 bg-[#CEB46A] text-black font-medium rounded hover:bg-[#b9a05a] transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Opening...' : 'Open W-9 Form'}
                  </button>
                  {!isSignupMode && (!profile || !profile.accessToken) && (
                    <p className="text-red-400 text-xs text-center">
                      Please log in to access W-9 form
                    </p>
                  )}
                  {isSignupMode && !signupFormData && (
                    <p className="text-red-400 text-xs text-center">
                      Form data is required
                    </p>
                  )}

                  <p className="text-gray-400 text-xs text-center">
                    The form will open in a popup window. Complete and submit it, then close the popup.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Inline version (default)
  return (
    <div className="bg-[#1C1C1E] rounded-lg p-6">
      <h3 className="text-lg font-semibold text-[#CEB46A] mb-4">
        Tax Information (W-9) Required
      </h3>

      {w9Status.w9_completed ? (
        <div className="p-4 bg-green-900 bg-opacity-30 border border-green-700 rounded">
          <div className="flex items-center gap-2 text-green-400">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-semibold">W-9 Form Completed</span>
          </div>
          {w9Status.w9_completion_date && (
            <p className="text-gray-400 text-sm mt-2">
              Completed on: {new Date(w9Status.w9_completion_date).toLocaleDateString()}
            </p>
          )}
        </div>
      ) : (
        <>
          <p className="text-gray-300 text-sm mb-4">
            As a contributor, you must complete the W-9 form for tax purposes before receiving payments.
            This form collects your legal name, TIN (SSN/EIN), entity type, and electronic signature.
          </p>

          <button
            onClick={openW9Popup}
            disabled={loading || (!isSignupMode && (!profile || !profile.accessToken)) || (isSignupMode && !signupFormData)}
            className="w-full py-3 px-4 bg-[#CEB46A] text-black font-medium rounded hover:bg-[#b9a05a] transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Opening W-9 Form...' : 'Open W-9 Form'}
          </button>
          {!isSignupMode && (!profile || !profile.accessToken) && (
            <p className="text-red-400 text-xs mt-2 text-center">
              Please log in to access W-9 form
            </p>
          )}
          {isSignupMode && !signupFormData && (
            <p className="text-red-400 text-xs mt-2 text-center">
              Form data is required
            </p>
          )}

          <p className="text-gray-400 text-xs mt-3 text-center">
            The form will open in a popup window. Complete and submit it, then close the popup when done.
          </p>
        </>
      )}
    </div>
  );
}

